/**
 * Render-time wrapper for a run of consecutive same-database pinned siblings.
 *
 * - When `run.isPinnedDbAlone === true`: renders children with no wrapper at
 *   all (the pinned-DB row stands alone in the tree).
 * - Otherwise: renders a subtle tinted block. A 7px uppercase database-name
 *   header is shown above the children iff `run.showHeader === true`.
 *
 * When `databaseContextMenu` is provided, right-clicking the header opens the
 * same context menu as the canonical database node (Refresh, Script New…,
 * backup/restore, clone, etc.).
 *
 * Spec: Docs/superpowers/specs/2026-05-26-object-explorer-pinning-design.md
 *   §"Visual grouping — database area (render-time wrapper)"
 */

import type { ReactNode } from 'react';
import type { PinnedAreaRun } from '../../../database-objects/services/pinnedObjects/groupPinnedChildrenByDatabase';
import type { ContextMenuAction } from '../../models/TreeModel';
import { TreeNodeContextMenu } from './TreeNodeContextMenu';

export interface PinnedAreaDatabaseContextMenu {
  actions: ContextMenuAction[];
  onAction: (actionId: string) => void;
}

export function PinnedAreaShell({
  run,
  children,
  databaseContextMenu,
}: {
  run: PinnedAreaRun;
  children: ReactNode;
  databaseContextMenu?: PinnedAreaDatabaseContextMenu;
}) {
  if (run.isPinnedDbAlone) {
    return <>{children}</>;
  }

  const header = run.showHeader ? (
    <div
      data-testid="pinned-area-header"
      className="relative px-3 pt-0.5 text-[7px] font-semibold uppercase tracking-widest text-indigo-600/80 dark:text-indigo-300/80"
    >
      {run.database}
    </div>
  ) : null;

  return (
    <div
      data-testid="pinned-area-shell"
      data-database={run.database}
      className="relative py-0.5"
    >
      {/* Tint layer is inset left/right so only the background has margins —
          the rows themselves stay full-width and vertically aligned with rows
          outside the area. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-1.5 right-1.5 rounded-md bg-indigo-500/[0.05] dark:bg-indigo-400/[0.07]"
      />
      {header &&
        (databaseContextMenu ? (
          <TreeNodeContextMenu
            actions={databaseContextMenu.actions}
            getActionHandler={(actionId) => (e) => {
              e.stopPropagation();
              databaseContextMenu.onAction(actionId);
            }}
          >
            {header}
          </TreeNodeContextMenu>
        ) : (
          header
        ))}
      <div className="relative">{children}</div>
    </div>
  );
}
